/**
 * Die einmalige Übernahme aus dem XLSX-Export der alten Tabelle.
 *
 * ORIGINALTEXT MARVIN (02.09.2026)
 *   „Abschließend müssen wir das Fehler mit der datendifferenz beheben und
 *    die Schnittstelle für den einmaligen Import aller Daten bauen."
 *
 * EINE KARTE UNTEN AUF „Unterlagen und Import", zugeklappt, nur für den, der
 * Tabellen anwenden darf. Kein eigener Menüpunkt: ein Menüpunkt für eine
 * Sache, die man einmal tut, ist ab dem zweiten Tag eine Falle.
 *
 * ZWEI SCHRITTE, WIE BEIM TELEKOM-IMPORT: erst die Vorschau (je Register,
 * was neu ist, was sich ändert, was übersprungen wird - mit Grund), dann
 * „Anwenden". Der Server rechnet den Plan beim Anwenden neu; die Datei geht
 * deshalb ein zweites Mal hinaus und bleibt hier im Speicher.
 */
import { useEffect, useState } from 'react';
import { api, datum, zeitpunkt } from '../api.ts';
import Abwurf from '../teile/Abwurf.tsx';
import { pakete, type Baumdatei } from '../teile/ordnerabwurf.ts';
import { verkleinern } from '../teile/bildverkleinern.ts';
import { ausFehler, Dialog, Fehlerkasten, type Fehlerlage } from '../teile/Formular.tsx';
import { useFrisch } from '../teile/Frisch.tsx';
import { fragen } from '../teile/rueckfrage.tsx';

const UEBER: Record<string, string> = {
  kennung: 'über die Kennung', anschrift: 'über die Anschrift',
  haus: 'über das Haus (Ort weicht ab)', neu: 'neu',
};
const WAS: Record<string, { text: string; klasse: string }> = {
  neu: { text: 'neu', klasse: 'z-laufend' },
  geaendert: { text: 'geändert', klasse: 'z-neu' },
  unveraendert: { text: 'unverändert', klasse: 'z-erledigt' },
  uebersprungen: { text: 'übersprungen', klasse: 'z-ueberfaellig' },
};

/**
 * DER REPORT: welche Aufträge neu sind, welche geändert.
 *
 * ORIGINALTEXT MARVIN (02.09.2026)
 *   „Das System muss mir dann beim hochladen der Liste einen Report ausgeben
 *    welche Aufträge neu sind damit ich die Ordner hochladen kann."
 *   „Auch bei den Aufträgen wo sich was geändert hat brauche ich die Info im
 *    Report denn da muss ich die Dateien ja auch nochmal hochladen."
 *
 * ZAHLEN BEANTWORTEN DIE FRAGE NICHT, DIE DANACH KOMMT. „87 neu" sagt nicht,
 * WELCHE - und genau die sucht jemand, der gleich die Ordner heraussuchen
 * will. Deshalb steht in jeder Zeile der ORDNERNAME der alten Ablage: die
 * Anschrift allein müsste er selbst in einen Ordnernamen übersetzen.
 */
export function Report({ lauf, zu }: { lauf: string; zu?: () => void }) {
  const [r, setR] = useState<any>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [zeige, setZeige] = useState<'neu' | 'geaendert'>('neu');

  useEffect(() => {
    api.uebernahmeReport(lauf).then(setR).catch((f) => setFehler(ausFehler(f)));
  }, [lauf]);

  if (fehler) return <Fehlerkasten lage={fehler} />;
  if (!r) return <p className="klein leise">Der Report wird geladen.</p>;

  const liste: any[] = zeige === 'neu' ? r.neu : r.geaendert;
  return (
    <div style={{ marginTop: 10 }}>
      <div className="reihe" style={{ flexWrap: 'wrap' }}>
        <button className="knopf" aria-current={zeige === 'neu'}
                onClick={() => setZeige('neu')}>
          {r.neu.length} neu
        </button>
        <button className="knopf" aria-current={zeige === 'geaendert'}
                onClick={() => setZeige('geaendert')}>
          {r.geaendert.length} geändert
        </button>
        {/* ZUM MITNEHMEN. Wer die Ordner heraussucht, steht dabei nicht vor
            diesem Bildschirm, sondern vor seiner Ablage. */}
        <a className="knopf" href={api.uebernahmeReportCsv(lauf)} target="_blank" rel="noreferrer">
          Als CSV herunterladen
        </a>
        <div className="wachsen" />
        {zu && <button className="knopf" onClick={zu}>Report schließen</button>}
      </div>
      <p className="klein leise" style={{ margin: '8px 0 0' }}>
        Für diese Aufträge fehlen die Dateien noch. Der Ordnername ist der, unter
        dem die Baustelle in der alten Ablage liegt - er lässt sich unten in
        „Dateien aus der alten Ablage" abwerfen. Sortiert ist die Liste nach
        diesem Namen, also so, wie die Ordner in der alten Ablage stehen.
      </p>
      {liste.length === 0 ? (
        <p className="klein leise" style={{ marginTop: 8 }}>
          {zeige === 'neu' ? 'Dieser Lauf hat keinen Auftrag angelegt.'
                           : 'Dieser Lauf hat keinen Auftrag geändert.'}
        </p>
      ) : (
        <div className="rollt" style={{ marginTop: 8 }}>
          <table className="liste">
            <thead>
              <tr><th>Anschrift</th><th>KLS-ID</th><th>Ordner in der alten Ablage</th>
                  {zeige === 'geaendert' && <th>Geändert</th>}</tr>
            </thead>
            <tbody>
              {liste.map((x: any) => (
                <tr key={x.id} style={{ cursor: 'default' }}>
                  <td>{x.anschrift}<div className="klein leise">{x.ort}</div></td>
                  <td className="klein einfarbig">{x.kls_id || '-'}</td>
                  <td className="klein">
                    <code>{x.ordner}</code>
                    {/* Ein Mängelauftrag liegt auf derselben Anschrift wie ein
                        früherer und trägt keine KLS-ID: der Ordnername wäre
                        der des ANDEREN Auftrags. Wer das nicht danebenstehen
                        hat, lädt denselben Ordner zweimal hoch. */}
                    {x.maengelbeseitigung && (
                      <div className="klein leise">
                        Mängelbeseitigung: dafür gibt es in der alten Ablage
                        keinen eigenen Ordner.
                      </div>
                    )}
                  </td>
                  {zeige === 'geaendert' && (
                    <td className="klein leise">{(x.felder ?? []).join(', ')}</td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Kachel({ wert, was, farbe }: { wert: number; was: string; farbe?: string }) {
  return (
    <div className="kachel">
      <div className="wert" style={farbe ? { color: `var(--${farbe})` } : undefined}>{wert}</div>
      <div className="was">{was}</div>
    </div>
  );
}

export default function Uebernahme() {
  const [datei, setDatei] = useState<File | null>(null);
  const [art, setArt] = useState<'tabelle_gewinnt' | 'nur_fuellen'>('tabelle_gewinnt');
  const [plan, setPlan] = useState<any>(null);
  const [ergebnis, setErgebnis] = useState<any>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [laeufe, setLaeufe] = useState<any[]>([]);
  const [zeigeZeilen, setZeigeZeilen] = useState<'geaendert' | 'alle' | 'keine'>('keine');
  const [report, setReport] = useState<string | null>(null);

  async function laden() {
    try { setLaeufe(await api.uebernahmeLaeufe()); } catch { /* kein Recht: dann steht die Karte gar nicht da */ }
  }
  useEffect(() => { laden(); }, []);
  useFrisch(laden);

  async function vorschau(d: File, a = art) {
    setLaeuft(true); setFehler(null); setErgebnis(null); setPlan(null);
    try { setPlan(await api.uebernahmeVorschau(d, a)); }
    catch (f) { setFehler(ausFehler(f)); }
    finally { setLaeuft(false); }
  }
  async function anwenden() {
    if (!datei) return;
    setLaeuft(true); setFehler(null);
    try {
      const e = await api.uebernahmeAnwenden(datei, art);
      setErgebnis(e); setPlan(null); setDatei(null);
      await laden();
    } catch (f) { setFehler(ausFehler(f)); }
    finally { setLaeuft(false); }
  }

  const auftraege = plan?.auftraege;
  // Die Aufträge zuerst: sie sind das Register, um das es geht.
  const register: [string, any][] = plan
    ? Object.entries(plan.register).sort(([a], [b]) => (a === 'auftraege' ? -1 : b === 'auftraege' ? 1 : 0)) : [];
  const zeilen: any[] = auftraege?.zeilen ?? [];
  const gezeigt = zeigeZeilen === 'alle' ? zeilen
    : zeigeZeilen === 'geaendert' ? zeilen.filter((z) => z.was !== 'unveraendert') : [];

  return (
    <div className="karte" style={{ marginTop: 14 }}>
      <h3>Übernahme aus der alten Tabelle (einmalig)</h3>
      <div className="leib">
        <p className="leise klein" style={{ marginTop: 0 }}>
          Der XLSX-Export der alten Tabelle, ein Tabellenblatt je Register. Erst die
          Vorschau, dann „Anwenden". Wiedererkannt wird ein Auftrag über seine Kennung,
          dann über die Anschrift, dann über das Haus. Leer überschreibt nie.
        </p>
        <Fehlerkasten lage={fehler} />

        <div className="reihe" style={{ marginBottom: 10 }}>
          <label className="feldpaar">
            <input type="radio" name="uebernahme_art" checked={art === 'tabelle_gewinnt'}
              onChange={() => { setArt('tabelle_gewinnt'); if (datei) vorschau(datei, 'tabelle_gewinnt'); }} />
            Die Tabelle gewinnt (leer überschreibt nie)
          </label>
          <label className="feldpaar">
            <input type="radio" name="uebernahme_art" checked={art === 'nur_fuellen'}
              onChange={() => { setArt('nur_fuellen'); if (datei) vorschau(datei, 'nur_fuellen'); }} />
            Nur leere Felder füllen
          </label>
        </div>

        <Abwurf
          titel={datei ? datei.name : 'Export der alten Tabelle (XLSX)'}
          hinweis="Genau eine Datei. Es wird noch nichts geschrieben: zuerst kommt die Vorschau."
          accept=".xlsx"
          klein
          laeuft={laeuft}
          laufText={plan ? 'Wird angewendet' : 'Wird gelesen'}
          nimm={(st) => { const d = st[0]?.datei; if (d) { setDatei(d); vorschau(d); } }}
        />

        {/* ------------------------------------------------ Das Ergebnis */}
        {ergebnis && (
          <div className="hinweis erledigt" style={{ marginTop: 12 }}>
            <b>Übernommen.</b>{' '}
            {Object.entries(ergebnis.zaehler ?? {}).map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`).join(', ') || 'Es gab nichts zu schreiben.'}
            {ergebnis.zusammengelegt?.length > 0 && (
              <div className="klein" style={{ marginTop: 4 }}>
                In ihrem Gegenstück aufgegangen: {ergebnis.zusammengelegt.join('; ')}.
              </div>
            )}
            {ergebnis.bleibenStehen?.length > 0 && (
              <div className="klein" style={{ marginTop: 4 }}>
                Doppelgänger mit Arbeit daran, die stehen bleiben: {ergebnis.bleibenStehen.join('; ')}.
              </div>
            )}
          </div>
        )}

        {/* DER REPORT STEHT SOFORT DA, nicht hinter einem Knopf. Er ist die
            Antwort auf „und was mache ich jetzt": die Ordner heraussuchen. */}
        {ergebnis?.id && <Report lauf={ergebnis.id} />}

        {/* ------------------------------------------------ Die Vorschau */}
        {plan && (
          <>
            {auftraege && (
              <div className="kachelreihe" style={{ marginTop: 14 }}>
                <Kachel wert={auftraege.zahlen.neu} was="Aufträge neu" farbe="laufend" />
                <Kachel wert={auftraege.zahlen.geaendert} was="Aufträge geändert" farbe="neu" />
                <Kachel wert={auftraege.zahlen.unveraendert} was="bleiben, wie sie sind" />
                <Kachel wert={auftraege.zahlen.uebersprungen} was="übersprungen" farbe={auftraege.zahlen.uebersprungen ? 'ueberfaellig' : undefined} />
                <Kachel wert={auftraege.doppelgaenger.length} was="Doppelgänger gehen auf" farbe={auftraege.doppelgaenger.length ? 'neu' : undefined} />
              </div>
            )}
            {auftraege && (
              <div className="klein leise" style={{ marginTop: 8 }}>
                Wiedererkannt: {auftraege.zahlen.ueber_kennung} über die Kennung,{' '}
                {auftraege.zahlen.ueber_anschrift} über die Anschrift,{' '}
                {auftraege.zahlen.ueber_haus} über das Haus (der Ort steht anders).
                {' '}Dazu {auftraege.zahlen.termine} Termine und {auftraege.zahlen.infos} Auftragsinfos.
                {auftraege.fehlen.length > 0 && (
                  <> {auftraege.fehlen.length} Aufträge stehen hier und nicht in der Tabelle; sie bleiben, wie sie sind.</>
                )}
              </div>
            )}
            {auftraege?.firmenUnbekannt?.length > 0 && (
              <div className="hinweis warnung" style={{ marginTop: 10 }}>
                Diese Firmen stehen an Terminen in der Tabelle und sind hier unbekannt:{' '}
                {auftraege.firmenUnbekannt.join(', ')}. Die Termine kommen ohne Firma;
                legen Sie die Firma unter Stammdaten an und übernehmen Sie danach noch einmal.
              </div>
            )}
            {auftraege?.felder?.length > 0 && (
              <div className="rollt" style={{ marginTop: 10 }}>
                <table className="liste">
                  <thead><tr><th>Feld</th><th className="zahl">Aufträge</th><th>Beispiel</th></tr></thead>
                  <tbody>
                    {auftraege.felder.map((f: any) => (
                      <tr key={f.feld} style={{ cursor: 'default' }}>
                        <td>{f.titel}</td>
                        <td className="zahl">{f.anzahl}</td>
                        <td className="klein leise">aus „{f.alt || '(leer)'}" wird „{f.neu}"</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="rollt" style={{ marginTop: 12 }}>
              <table className="liste">
                <thead>
                  <tr><th>Register</th><th className="zahl">gelesen</th><th className="zahl">neu</th>
                      <th className="zahl">geändert</th><th className="zahl">unverändert</th><th className="zahl">übersprungen</th></tr>
                </thead>
                <tbody>
                  {register.map(([k, s]) => (
                    <tr key={k} style={{ cursor: 'default' }}>
                      <td>
                        {s.titel}
                        {s.hinweise?.length > 0 && (
                          <details className="klein" style={{ marginTop: 4 }}>
                            <summary>{s.hinweise.length === 1 ? '1 Hinweis' : `${s.hinweise.length} Hinweise`}</summary>
                            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                              {s.hinweise.map((h: any, i: number) => (
                                <li key={i}>{h.zeile ? `Zeile ${h.zeile}: ` : ''}{h.text}</li>
                              ))}
                            </ul>
                          </details>
                        )}
                      </td>
                      <td className="zahl">{s.gelesen}</td>
                      <td className="zahl">{s.neu}</td>
                      <td className="zahl">{s.geaendert}</td>
                      <td className="zahl">{s.unveraendert}</td>
                      <td className="zahl" style={s.uebersprungen ? { color: 'var(--ueberfaellig)' } : undefined}>{s.uebersprungen}</td>
                    </tr>
                  ))}
                  {plan.uebergangen.map((u: any) => (
                    <tr key={u.tabellenblatt} style={{ cursor: 'default' }}>
                      <td className="leise">{u.tabellenblatt}<div className="klein">{u.grund}</div></td>
                      <td className="zahl leise" colSpan={5}>nicht gelesen</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {auftraege?.doppelgaenger?.length > 0 && (
              <details style={{ marginTop: 10 }}>
                <summary>{auftraege.doppelgaenger.length} Doppelgänger gehen in ihrem Gegenstück auf (nur, wenn nichts daran hängt)</summary>
                <ul className="klein" style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                  {auftraege.doppelgaenger.map((d: any) => (
                    <li key={d.auftragId}>{d.anschrift}, {d.ort} → {d.bleibtAnschrift}</li>
                  ))}
                </ul>
              </details>
            )}

            {zeilen.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div className="reihe">
                  <button className="knopf" onClick={() => setZeigeZeilen(zeigeZeilen === 'geaendert' ? 'keine' : 'geaendert')}>
                    {zeigeZeilen === 'geaendert' ? 'Zeilen ausblenden' : 'Geänderte Aufträge zeigen'}
                  </button>
                  <button className="knopf" onClick={() => setZeigeZeilen(zeigeZeilen === 'alle' ? 'keine' : 'alle')}>
                    {zeigeZeilen === 'alle' ? 'Zeilen ausblenden' : 'Alle Zeilen zeigen'}
                  </button>
                </div>
                {gezeigt.length > 0 && (
                  <div className="rollt" style={{ marginTop: 8 }}>
                    <table className="liste">
                      <thead><tr><th>Zeile</th><th>Anschrift</th><th>Was</th><th>Wiedererkannt</th><th>Änderungen</th></tr></thead>
                      <tbody>
                        {gezeigt.map((z: any) => (
                          <tr key={z.zeile} style={{ cursor: 'default' }}>
                            <td className="zahl">{z.zeile}</td>
                            <td>{z.anschrift}<div className="klein leise">{z.ort}</div></td>
                            <td><span className={'marke-zustand ' + WAS[z.was].klasse}>{WAS[z.was].text}</span></td>
                            <td className="klein">{UEBER[z.ueber]}</td>
                            <td className="klein">
                              {[
                                z.felder.map((f: any) => f.titel).join(', '),
                                ...z.gewerke.filter((g: any) => g.felder.length).map((g: any) => `${g.art}: ${g.felder.map((f: any) => f.titel).join(', ')}`),
                                z.infoNeu ? `${z.infoNeu} Auftragsinfo${z.infoNeu === 1 ? '' : 's'}` : '',
                                z.kennungenNeu.length ? `${z.kennungenNeu.length} Kennung${z.kennungenNeu.length === 1 ? '' : 'en'}` : '',
                              ].filter(Boolean).join(', ')}
                              {z.hinweise.length > 0 && <div style={{ color: 'var(--ueberfaellig)' }}>{z.hinweise.join(' ')}</div>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            <div className="reihe" style={{ marginTop: 14 }}>
              <button className="knopf haupt" onClick={anwenden} disabled={laeuft || !datei}>
                {laeuft ? 'Läuft' : 'Anwenden'}
              </button>
              <span className="klein leise">Schreibt alles in einem Zug. Geht etwas schief, wird nichts geschrieben.</span>
            </div>
          </>
        )}

        {/* ------------------------------------------------ Die Läufe */}
        {laeufe.length > 0 && (
          <details style={{ marginTop: 14 }}>
            <summary className="klein">{laeufe.length} frühere Läufe</summary>
            {/* UND AUCH DIESE TABELLE ROLLT.
                Sie steht in einem `details`, und ein `details` erscheint erst,
                wenn es LÄUFE gibt - vor dem 04.09.2026 gab es auf der
                Prüfdatenbank nie welche, und Prüfstand 014 hat sie deshalb nie
                gesehen. Sechs Tabellen auf diesem Bildschirm tragen ihr
                `rollt`, diese beiden nicht. **Ein Bildschirmteil, den es nur
                mit Daten gibt, wird ohne Daten nicht gemessen.** */}
            <div className="rollt">
            <table className="liste" style={{ marginTop: 6 }}>
              <thead><tr><th>Wann</th><th>Datei</th><th>Wer</th><th>Aufträge</th>
                         <th className="knopfleiste">Report</th></tr></thead>
              <tbody>
                {laeufe.map((l) => (
                  <tr key={l.id} style={{ cursor: 'default' }}>
                    <td className="klein">{zeitpunkt(l.begonnen_am)}</td>
                    <td className="klein">{l.datei}</td>
                    <td className="klein">{l.benutzer ?? ''}</td>
                    <td className="klein">
                      {l.register?.auftraege
                        ? `${l.register.auftraege.neu} neu, ${l.register.auftraege.geaendert} geändert`
                        : (l.beendet_am ? 'ohne Register „Aufträge"' : 'abgebrochen am ' + datum(l.begonnen_am))}
                    </td>
                    {/* EIN KNOPF, DEN ES NUR GIBT, WENN ER ETWAS ÖFFNET. Läufe
                        vor .81 haben keine Liste mitgeschrieben; ein Knopf, der
                        auf eine leere Seite führt, ist schlimmer als keiner. */}
                    <td className="knopfleiste">
                      {l.im_report > 0 ? (
                        <button className="knopf"
                                onClick={() => setReport(report === l.id ? null : l.id)}>
                          {report === l.id ? 'zu' : `${l.im_report} Zeilen`}
                        </button>
                      ) : <span className="klein leise">-</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            {report && <Report lauf={report} zu={() => setReport(null)} />}
          </details>
        )}
      </div>
    </div>
  );
}


/**
 * Die Dateien aus der alten Ablage, als ganzer Ordner.
 *
 * ORIGINALTEXT MARVIN (02.09.2026)
 *   „Was ist mit dem Import der Daten in das roh Verzeichnis? Kopiere ich da
 *    wieder alles rein und wie sortiert er es zu den Aufträgen?"
 *
 * DAS ROHVERZEICHNIS GIBT ES NICHT MEHR. Die alte Ablage wird hier als Ordner
 * abgeworfen, so wie sie auf der Platte liegt. Der Browser liest den Baum
 * (`webkitGetAsEntry`, in `teile/ordnerabwurf.ts`), schickt zuerst nur die
 * PFADE zur Vorschau - der Server sagt je Auftragsordner, welcher Auftrag
 * das ist oder warum keiner passt - und dann die Bytes in Paketen, mit einem
 * Zähler dazwischen. Ein Abbruch nach dem dreissigsten Paket kostet nicht
 * die ersten neunundzwanzig.
 */
export function Ordneruebernahme() {
  const [baum, setBaum] = useState<Baumdatei[]>([]);
  /* Ein hochgeladenes ZIP: die Prüfsumme, unter der es beim Server liegt.
     Es geht EINMAL hoch - bei einer halben Gigabyte ist „schicken Sie es
     noch einmal" keine Antwort. */
  const [archiv, setArchiv] = useState<{ sha: string; name: string; dateien: number } | null>(null);
  const [plan, setPlan] = useState<any>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [stand, setStand] = useState<{ paket: number; pakete: number; dateien: number } | null>(null);
  const [zaehler, setZaehler] = useState<Record<string, number>>({});
  const [gruende, setGruende] = useState<any[]>([]);
  const [ergebnis, setErgebnis] = useState<any>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [laeufe, setLaeufe] = useState<any[]>([]);

  async function laden() {
    try { setLaeufe(await api.ordnerLaeufe()); } catch { /* ohne Recht steht die Karte nicht da */ }
  }
  useEffect(() => { laden(); }, []);
  useFrisch(laden);

  async function vorschau(st: Baumdatei[]) {
    setFehler(null); setErgebnis(null); setPlan(null); setZaehler({}); setGruende([]);
    setArchiv(null); setBaum([]);
    if (!st.length) {
      setFehler({ text: 'Aus diesem Abwurf kam keine einzige Datei. Manche Browser geben '
                      + 'abgelegte Ordner nicht heraus; der Tipp auf die Fläche nimmt sie sicher.' });
      return;
    }
    /* EIN ARCHIV IST EINE DATEI UND KEIN BAUM.
     *
     * ORIGINALTEXT MARVIN (02.09.2026)
     *   „Kann ich die Ordner als zip oder Rar hochladen?"
     *
     * Wer ein ZIP auf dieselbe Fläche zieht, meint dasselbe wie mit einem
     * Ordner. Erkannt wird es hier an der Endung (die Entscheidung, ob es
     * WIRKLICH ein ZIP ist, trifft der Server am Inhalt) - ein zweites
     * Abwurffeld daneben wäre wieder eine zweite Bauart für eine Handbewegung.
     */
    if (st.length === 1 && /\.(zip|rar|7z|tar|gz)$/i.test(st[0].datei.name)) {
      setLaeuft(true);
      try {
        const a = await api.ordnerArchiv(st[0].datei);
        setArchiv({ sha: a.archiv, name: a.name, dateien: a.dateien });
        setPlan(a);
      } catch (f) { setFehler(ausFehler(f)); }
      finally { setLaeuft(false); }
      return;
    }
    setBaum(st); setLaeuft(true);
    try { setPlan(await api.ordnerVorschau(st.map((x) => x.pfad))); }
    catch (f) { setFehler(ausFehler(f)); }
    finally { setLaeuft(false); }
  }

  async function anwenden() {
    if ((!baum.length && !archiv) || !plan) return;
    setLaeuft(true); setFehler(null); setErgebnis(null);
    const z: Record<string, number> = { abgelegt: 0, schon_da: 0, nicht_zugeordnet: 0, uebersprungen: 0 };
    const g: any[] = [];
    const zaehle = (liste: any[]) => {
      for (const e of liste) {
        z[e.was] = (z[e.was] ?? 0) + 1;
        if (e.was !== 'abgelegt') g.push(e);
      }
      setZaehler({ ...z }); setGruende([...g]);
    };

    // ---- Der Weg über das ARCHIV: die Bytes liegen schon beim Server.
    if (archiv) {
      try {
        const { lauf } = await api.ordnerBeginn(archiv.dateien);
        let von = 0;
        for (;;) {
          const a = await api.ordnerArchivPaket(lauf, archiv.sha, von);
          zaehle(a.ergebnisse);
          setStand({ paket: Math.ceil(a.bis / 60) || 1,
                     pakete: Math.ceil(a.gesamt / 60) || 1, dateien: a.bis });
          if (a.fertig) break;
          von = a.bis;
        }
        setErgebnis(await api.ordnerEnde(lauf));
        await api.ordnerArchivWeg(archiv.sha).catch(() => { /* der Zwischenspeicher räumt selbst */ });
        setPlan(null); setArchiv(null);
        await laden();
      } catch (f) { setFehler(ausFehler(f)); }
      finally { setLaeuft(false); setStand(null); }
      return;
    }

    try {
      const { lauf } = await api.ordnerBeginn(baum.length);
      // Nur, was der Plan einem Auftrag zuordnet, geht überhaupt hinaus:
      // eine Datei ohne Auftrag kostet sonst die Übertragung und bleibt
      // trotzdem liegen. Der Rest steht in der Vorschau, mit Grund.
      const geht = new Set(plan.ordner.filter((o: any) => o.zuordnung.ok).map((o: any) => o.auftragsordner));
      const mit = baum.filter((b) => {
        const glieder = b.pfad.split('/');
        return glieder.some((t) => geht.has(t));
      });
      const stuecke = pakete(mit);
      let n = 0;
      for (let i = 0; i < stuecke.length; i++) {
        setStand({ paket: i + 1, pakete: stuecke.length, dateien: n });
        const kleiner: { datei: File; pfad: string }[] = [];
        for (const x of stuecke[i]) kleiner.push({ pfad: x.pfad, datei: await verkleinern(x.datei) });
        const a = await api.ordnerPaket(lauf, kleiner);
        zaehle(a.ergebnisse);
        n += stuecke[i].length;
      }
      setErgebnis(await api.ordnerEnde(lauf));
      setPlan(null); setBaum([]);
      await laden();
    } catch (f) { setFehler(ausFehler(f)); }
    finally { setLaeuft(false); setStand(null); }
  }

  const ohneAuftrag = plan?.ordner.filter((o: any) => !o.zuordnung.ok) ?? [];
  const mitAuftrag = plan?.ordner.filter((o: any) => o.zuordnung.ok) ?? [];

  return (
    <div className="karte" style={{ marginTop: 14 }}>
      <h3>Dateien aus der alten Ablage (einmalig)</h3>
      <div className="leib">
        <p className="leise klein" style={{ marginTop: 0 }}>
          Die alte Ablage als GANZER Ordner, so wie sie auf der Platte liegt. Der Name
          des Auftragsordners (Anschrift, KLS-ID) sagt, zu welchem Auftrag die Dateien
          gehören; der Unterordner sagt, wohin im Auftrag. Erst die Vorschau, dann
          „Anwenden". Was schon am Auftrag hängt, wird nicht noch einmal abgelegt.
        </p>
        <Fehlerkasten lage={fehler} />

        <Abwurf
          titel={archiv ? `${archiv.name}: ${archiv.dateien} Dateien im Archiv`
                        : baum.length ? `${baum.length} Dateien gelesen`
                        : 'Die alte Ablage (Ordner oder ZIP)'}
          hinweis={<>
            Ein Ordner mit den Auftragsordnern darin - oder eine ZIP-Datei mit
            denselben Ordnern. Es wird noch nichts geschrieben: zuerst kommt die
            Vorschau. <b>RAR geht nicht</b>, dafür bräuchte dieses System ein
            fremdes Programm, das jede hereinkommende Datei anfasst.
          </>}
          ordner
          klein
          laeuft={laeuft}
          laufText={stand
            ? `Paket ${stand.paket} von ${stand.pakete}, ${stand.dateien} Dateien durch`
            : (plan ? 'Wird angewendet' : 'Wird gelesen')}
          nimm={vorschau}
        />
        {/* DER ZWEITE WEG FÜR DASSELBE, und er ist kein zweiter Weg: die
            Fläche darüber nimmt einen ORDNER (der Tipp öffnet einen
            Ordnerwähler), diese Zeile nimmt eine DATEI. Ein Ordnerwähler zeigt
            keine ZIP-Datei an - wer eine hochladen will, käme sonst nicht an
            sie heran. */}
        <p className="klein leise" style={{ margin: '8px 0 0' }}>
          Eine ZIP-Datei statt eines Ordners:{' '}
          <label className="knopf" style={{ display: 'inline-flex' }}>
            Archiv wählen
            <input type="file" accept=".zip" style={{ display: 'none' }}
                   disabled={laeuft}
                   onChange={(e) => {
                     const d = e.target.files?.[0];
                     e.target.value = '';
                     if (d) vorschau([{ datei: d, pfad: d.name }]);
                   }} />
          </label>
        </p>

        {(laeuft && stand) && (
          <div className="klein leise" style={{ marginTop: 8 }}>
            Bisher: {zaehler.abgelegt ?? 0} abgelegt, {zaehler.schon_da ?? 0} lagen schon da,
            {' '}{zaehler.nicht_zugeordnet ?? 0} ohne Auftrag, {zaehler.uebersprungen ?? 0} übersprungen.
          </div>
        )}

        {ergebnis && (
          <div className="hinweis erledigt" style={{ marginTop: 12 }}>
            <b>Übernommen.</b>{' '}
            {ergebnis.abgelegt} abgelegt, {ergebnis.schon_da} lagen schon da,
            {' '}{ergebnis.nicht_zugeordnet} ohne Auftrag, {ergebnis.uebersprungen} übersprungen
            {' '}(von {ergebnis.angekuendigt} im Ordner).
            {gruende.length > 0 && (
              <details className="klein" style={{ marginTop: 6, fontWeight: 400 }}>
                <summary>{gruende.length} Dateien mit Grund</summary>
                <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                  {gruende.map((e, i) => <li key={i}>{e.pfad}: {e.grund}</li>)}
                </ul>
              </details>
            )}
          </div>
        )}

        {plan && (
          <>
            <div className="kachelreihe" style={{ marginTop: 14 }}>
              <Kachel wert={plan.dateien} was="Dateien im Ordner" />
              <Kachel wert={mitAuftrag.length} was="Auftragsordner erkannt" farbe="erledigt" />
              <Kachel wert={ohneAuftrag.length} was="Ordner ohne Auftrag" farbe={ohneAuftrag.length ? 'ueberfaellig' : undefined} />
              <Kachel wert={plan.zugeordnet} was="Dateien gehen hinein" farbe="laufend" />
              <Kachel wert={plan.nichtZugeordnet + plan.muell} was="bleiben draußen" farbe={plan.nichtZugeordnet ? 'ueberfaellig' : undefined} />
            </div>
            {plan.ohneOrdner > 0 && (
              <div className="hinweis warnung" style={{ marginTop: 10 }}>
                {plan.ohneOrdner} Dateien liegen in keinem Auftragsordner (kein Glied im Pfad
                sieht aus wie eine Anschrift oder nennt eine KLS-ID). Sie bleiben draußen.
              </div>
            )}

            {/* WAS AUS DEM REPORT FEHLT, IST DIE EIGENTLICHE AUSKUNFT.
                Marvin, 02.09.2026: „einen Report ... welche Aufträge neu sind
                damit ich die Ordner hochladen kann." Ein Auftrag, den die
                Übernahme angelegt hat und für den hier kein Ordner liegt,
                bekommt sonst nie Dateien - und es fällt niemandem auf. */}
            {plan.fehlenAusReport?.length > 0 && (
              <div className="hinweis warnung" style={{ marginTop: 10 }}>
                <b>{plan.fehlenAusReport.length} Aufträge aus der letzten Übernahme
                haben in diesem Abwurf keinen Ordner.</b>
                <details className="klein" style={{ marginTop: 6, fontWeight: 400 }}>
                  <summary>Welche das sind</summary>
                  <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                    {plan.fehlenAusReport.map((x: any, i: number) => (
                      <li key={i}>
                        <code>{x.ordner}</code>
                        {x.was === 'geaendert' && <span className="leise"> (geändert)</span>}
                      </li>
                    ))}
                  </ul>
                </details>
              </div>
            )}

            <div className="rollt" style={{ marginTop: 12 }}>
              <table className="liste">
                <thead>
                  <tr><th>Auftragsordner</th><th>Auftrag</th><th className="zahl">Dateien</th><th>Wohin</th></tr>
                </thead>
                <tbody>
                  {ohneAuftrag.map((o: any) => (
                    <tr key={o.auftragsordner} style={{ cursor: 'default' }}>
                      <td>{o.auftragsordner}</td>
                      <td style={{ color: 'var(--ueberfaellig)' }}>
                        {o.zuordnung.grund}
                        {o.zuordnung.kandidaten?.length > 0 && (
                          <div className="klein">In Frage kommen: {o.zuordnung.kandidaten.join('; ')}</div>
                        )}
                      </td>
                      <td className="zahl">{o.dateien}</td>
                      <td className="klein leise">bleiben draußen</td>
                    </tr>
                  ))}
                  {mitAuftrag.map((o: any) => (
                    <tr key={o.auftragsordner} style={{ cursor: 'default' }}>
                      <td>
                        {o.auftragsordner}
                        {/* AUS DER LETZTEN ÜBERNAHME. Damit sieht man beim
                            Abwerfen, ob man die richtigen Ordner erwischt
                            hat - ohne den Report daneben aufzuschlagen. */}
                        {plan.ausReport?.[o.auftragsordner] === 'neu' && (
                          <span className="marke-zustand z-laufend" style={{ marginLeft: 6 }}>neu</span>
                        )}
                        {plan.ausReport?.[o.auftragsordner] === 'geaendert' && (
                          <span className="marke-zustand z-neu" style={{ marginLeft: 6 }}>geändert</span>
                        )}
                      </td>
                      <td>
                        {plan.auftraege[o.zuordnung.auftragId] ?? o.zuordnung.auftragId}
                        <div className="klein leise">{o.zuordnung.wie}</div>
                      </td>
                      <td className="zahl">{o.dateien}{o.muell ? <span className="leise"> +{o.muell} Müll</span> : null}</td>
                      <td className="klein">
                        {o.ziele.map((z: any) => `${z.ordner} (${z.dateien})`).join(', ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="reihe" style={{ marginTop: 14 }}>
              <button className="knopf haupt" onClick={anwenden} disabled={laeuft || !plan.zugeordnet}>
                {laeuft ? 'Läuft' : `${plan.zugeordnet} Dateien übernehmen`}
              </button>
              <span className="klein leise">
                In Paketen, mit Zähler. {archiv
                  ? 'Das Archiv liegt schon beim Server; es wird Eintrag für Eintrag ausgepackt.'
                  : 'Bilder werden vorher verkleinert.'}{' '}
                Was ohne Auftrag ist, geht gar nicht erst hinaus. Was am Auftrag
                schon liegt - gleicher Inhalt oder gleicher Name im selben Ordner -
                wird gezählt und nicht doppelt abgelegt.
              </span>
            </div>
          </>
        )}

        {laeufe.length > 0 && (
          <details style={{ marginTop: 14 }}>
            <summary className="klein">{laeufe.length} frühere Läufe</summary>
            {/* UND AUCH DIESE TABELLE ROLLT.
                Sie steht in einem `details`, und ein `details` erscheint erst,
                wenn es LÄUFE gibt - vor dem 04.09.2026 gab es auf der
                Prüfdatenbank nie welche, und Prüfstand 014 hat sie deshalb nie
                gesehen. Sechs Tabellen auf diesem Bildschirm tragen ihr
                `rollt`, diese beiden nicht. **Ein Bildschirmteil, den es nur
                mit Daten gibt, wird ohne Daten nicht gemessen.** */}
            <div className="rollt">
            <table className="liste" style={{ marginTop: 6 }}>
              <thead><tr><th>Wann</th><th>Wer</th><th className="zahl">Dateien</th><th className="zahl">abgelegt</th><th className="zahl">schon da</th><th className="zahl">ohne Auftrag</th></tr></thead>
              <tbody>
                {laeufe.map((l) => (
                  <tr key={l.id} style={{ cursor: 'default' }}>
                    <td className="klein">{zeitpunkt(l.begonnen_am)}{!l.beendet_am && <span className="leise">, abgebrochen</span>}</td>
                    <td className="klein">{l.benutzer ?? ''}</td>
                    <td className="zahl">{l.angekuendigt}</td>
                    <td className="zahl">{l.abgelegt}</td>
                    <td className="zahl">{l.schon_da}</td>
                    <td className="zahl">{l.nicht_zugeordnet}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </details>
        )}
      </div>
    </div>
  );
}


/**
 * DAS TRANSFERVERZEICHNIS: die Auftragsordner in Masse, ohne Browser.
 *
 * ORIGINALTEXT MARVIN (05.09.2026)
 *   „mit dem umschalten bzw umzug des system brauche ich die möglichkeit die
 *    ganze ordner in masse über winscp hochzuladen, dann brauche ich die
 *    nicht alle über 400 ordner einzeln zippen und dann uploaden. sondern ich
 *    lade in ein transferverzeichnis die ganzen ordner hoch ... und dann ziehe
 *    ich das script rein und du suchst den ordner, überträgst den inhalt."
 *
 * **DER BRIEFKASTEN WIRD ÜBER WinSCP GEFÜLLT, DER LAUF STEHT HIER.** Das ist
 * der ganze Unterschied zum Rohverzeichnis, das am 27.08.2026 gefallen ist:
 * damals lag der LAUF auf der Kommandozeile, und der Bildschirm sagte auch
 * dazu auf. Hier wird angesehen, geprüft, übertragen und aufgeräumt aus der
 * Oberfläche.
 *
 * EIN ORDNER JE AUFRUF, und die Liste läuft von oben nach unten durch.
 * Vierhundert Ordner in einer Anfrage wären eine Stunde ohne ein Wort
 * dazwischen, und ein Abbruch nach fünfzig Minuten kostet alles - dieselbe
 * Überlegung wie bei den Paketen des Ordnerabwurfs.
 */
export function Transferverzeichnis() {
  const [stand, setStand] = useState<any>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [beiOrdner, setBeiOrdner] = useState<string | null>(null);
  const [ergebnisse, setErgebnisse] = useState<any[]>([]);
  const [waehlen, setWaehlen] = useState<string | null>(null);
  const [lauf, setLauf] = useState<any>(null);

  async function laden() {
    setLaeuft(true); setFehler(null);
    try { setStand(await api.transferLesen()); }
    catch (f) { setFehler(ausFehler(f)); }
    finally { setLaeuft(false); }
  }
  useEffect(() => { laden(); }, []);
  useFrisch(laden);

  /* ==========================================================================
   * DER LAUF LIEGT IM SERVER, UND DIESE SEITE SIEHT IHM NUR ZU.
   * ==========================================================================
   *
   *   ORIGINALTEXT MARVIN (06.09.2026, während die Ordner liefen)
   *     „ich habe die seite verlassen, er arbeitet zwar im hintergrund weiter,
   *      aber er zeigt mir nicht den aktellen stand an wo er dran ist. ich
   *      kann es auch nicht anhalten."
   *
   * Hier stand eine SCHLEIFE: `for (const o of liste) await api.transferLauf(o)`.
   * Wer die Seite verliess, nahm sie mit - der Server brachte den Ordner zu
   * Ende, an dem er gerade sass, und der nächste wurde nie angefordert. Und
   * der Stand stand in genau diesem Fenster: auf einem zweiten Rechner war
   * nichts zu sehen, und einen Halteknopf gab es nur hier.
   *
   * DER STAND WIRD JETZT GEHOLT UND NICHT GEZÄHLT. Zwei Sekunden Takt,
   * solange etwas läuft - und der Takt hört auf, sobald der Lauf fertig ist:
   * **ein Takt, der weiterläuft, wenn es nichts mehr zu holen gibt, ist eine
   * Anfrage je zwei Sekunden für nichts.**
   */
  async function standHolen() {
    try {
      const r = await api.transferMassenstand();
      setLauf(r.stand);
      return r.stand;
    } catch { return null; }
  }
  useEffect(() => { standHolen(); }, []);
  useEffect(() => {
    if (!lauf?.laeuft) return;
    const t = setInterval(async () => {
      const s = await standHolen();
      /* IST ER FERTIG, WIRD DIE LISTE FRISCH GEHOLT. Die übertragenen Ordner
         sind aus dem Verzeichnis verschwunden; eine Liste, die sie weiter
         zeigt, ist eine falsche Auskunft. */
      if (s && !s.laeuft) await laden();
    }, 2000);
    return () => clearInterval(t);
  }, [lauf?.laeuft]);

  async function alleUebertragen() {
    setFehler(null);
    try {
      const r = await api.transferMassenlauf();
      setLauf(r.stand);
      /* EIN SCHRITT, DER NICHTS ÄNDERT, IST KEIN FEHLER, ABER AUCH KEIN
         ERFOLG: wer drückt, während schon einer läuft, erfährt es. */
      if (!r.gestartet && r.grund) {
        setFehler({ fehler: r.grund, hinweis: null } as any);
      }
    } catch (f) { setFehler(ausFehler(f)); }
  }

  async function anhalten() {
    setFehler(null);
    try {
      const r = await api.transferMassenAnhalten();
      setLauf(r.stand);
    } catch (f) { setFehler(ausFehler(f)); }
  }

  async function einer(name: string) {
    setLaeuft(true); setFehler(null); setBeiOrdner(name);
    try {
      const r = await api.transferLauf(name);
      /* EIN ORDNER, EINE ZEILE: wer denselben Ordner noch einmal anstößt,
         sieht das neue Ergebnis statt einer Wand gleicher Zeilen. */
      setErgebnisse((a) => [...a.filter((x) => x.ordner !== r.ordner), r]);
      await laden();
    } catch (f) { setFehler(ausFehler(f)); }
    finally { setLaeuft(false); setBeiOrdner(null); }
  }

  /* DEN REST VERWERFEN (.271): nur, was die Plattform nicht annimmt. */
  async function restWeg(name: string, offen: any[]) {
    const liste = (offen ?? []).map((o: any) => String(o.pfad ?? '').split('/').pop()).slice(0, 8).join(', ');
    if (!await fragen(`In „${name}" bleiben Dateien liegen, die die Plattform nicht annimmt (${liste}). `
      + 'Diese Dateien löschen und den Ordner entfernen? Was angenommen würde, bleibt stehen.')) return;
    setLaeuft(true); setFehler(null);
    try {
      const r = await api.transferRest(name);
      setErgebnisse((a) => a.map((x) => x.ordner === name ? { ...x, offen: [], klartext: r.klartext } : x));
      await laden();
    } catch (f) { setFehler(ausFehler(f)); }
    finally { setLaeuft(false); }
  }

  const ordner: any[] = stand?.ordner ?? [];
  /* DREI HAUFEN. Mit Auftrag, mit einem Auftrag, der angelegt wird (Marvin,
     01.10.2026: „das sind ja alles alte aufträge, die sollst du ganz normal
     anlegen"), und der Rest, bei dem ein Mensch entscheiden muss. */
  const anlegen = ordner.filter((o) => !o.zuordnung.ok && o.anlage);
  const offen = ordner.filter((o) => !o.zuordnung.ok && !o.anlage);
  const bereit = ordner.filter((o) => o.zuordnung.ok);
  const zuUebertragen = bereit.length + anlegen.length;
  const mb = (b: number) => (b / 1024 / 1024).toFixed(1).replace('.', ',');

  return (
    <div className="karte" style={{ marginTop: 14 }}>
      <h3>Transferverzeichnis</h3>
      <div className="leib">
        <p className="leise klein" style={{ marginTop: 0 }}>
          Die Auftragsordner der alten Ablage über WinSCP in das Verzeichnis auf
          dem Server legen, dann hier übertragen. Jede Datei geht durch dieselbe
          Strecke wie ein Upload: Format geprüft, Bilder verkleinert, Ordner
          zugeordnet. Fehlt der Auftrag zu einem Ordner, wird er aus dem
          Ordnernamen angelegt. Was schon am Auftrag liegt, wird nicht doppelt
          abgelegt. Was nachweislich angekommen ist, wird im Transferverzeichnis
          entfernt.
        </p>

        {/* DER PFAD STEHT HIER, UND ZWAR IMMER.
         *
         * ORIGINALTEXT MARVIN (05.09.2026)
         *   „gebe mir nochmal den uploadpfad aus bzb schreibe ihn in dem
         *    menpüunkt für die uploads mit rein, wo der bereich ist um den
         *    altbestand zu übertragen."
         *
         * ZWEI FEHLER STANDEN HIER ÜBEREINANDER. Der Pfad erschien NUR,
         * wenn das Verzeichnis schon da war - also gerade dann nicht, wenn
         * man ihn braucht: vor dem ersten Hochladen. Und er nannte den Pfad
         * IM CONTAINER; WinSCP verbindet sich auf den SERVER, und dort gibt
         * es kein `/daten/transfer`.
         *
         * **Ein Pfad, der auf einem anderen Rechner gilt als dem, auf dem
         * man steht, ist kein Pfad, sondern eine Suche.** */}
        <div className="hinweis" style={{ marginTop: 0 }}>
          <b>Hierhin gehören die Ordner</b>
          <div className="klein">
            Über WinSCP auf den Server, in dieses Verzeichnis:
          </div>
          <p className="klein" style={{ margin: '6px 0 0' }}>
            <code>{stand?.serverpfad || '(steht nicht in der .env)'}</code>
          </p>
          {stand && !stand.serverpfad && (
            <p className="klein leise" style={{ margin: '4px 0 0' }}>
              Der Pfad auf dem Server steht in <code>TRANSFER_QUELLE</code> in
              der <code>.env</code> neben <code>docker-compose.yml</code>;
              <code>aktualisieren.sh</code> trägt ihn beim nächsten Lauf ein.
              Geraten wird er hier nicht.
            </p>
          )}
          <p className="klein leise" style={{ margin: '6px 0 0' }}>
            Im Container heisst dasselbe Verzeichnis{' '}
            <code>{stand?.wurzel ?? '/daten/transfer'}</code> (Schlüssel
            „transfer.verzeichnis"). Das ist der Pfad, den dieser Server sieht -
            in WinSCP gibt es ihn nicht.
          </p>
        </div>

        <Fehlerkasten lage={fehler} />

        {stand && !stand.da && (
          <div className="hinweis warnung">
            <b>Das Verzeichnis steht noch nicht bereit</b>{stand.grund}
          </div>
        )}

        {stand?.da && (
          <>
            <div className="kacheln">
              <Kachel wert={stand.summe.ordner} was="Ordner" />
              <Kachel wert={stand.summe.zugeordnet} was="mit Auftrag" farbe="gut" />
              <Kachel wert={anlegen.length} was="Auftrag wird angelegt" />
              <Kachel wert={offen.length} was="ohne Auftrag"
                      farbe={offen.length ? 'warnung' : undefined} />
              <Kachel wert={stand.summe.dateien} was="Dateien" />
            </div>
            <p className="klein leise">
              {/* Der Pfad steht oben, in der Karte, die ihn erklärt. Hier
                  bleibt, was sich WIRKLICH je Lauf ändert. */}
              {mb(stand.summe.bytes)} MB,{' '}
              {stand.loescht
                ? 'ein sauber übertragener Ordner wird danach entfernt'
                : 'die Quellordner bleiben stehen (Schalter „transfer.quelle_loeschen")'}
            </p>

            {/* EINE WERKZEUGLEISTE IST EINE ZEILE UND KEIN RASTER. Hier stand
                `knopfleiste breit`, also `repeat(auto-fill, minmax(104px,1fr))`:
                bei 1276 Pixeln Breite ergibt das ELF gleich breite Spalten fuer
                ZWEI Knoepfe, und der Knopf „346 zugeordnete Ordner uebertragen"
                bekam eine Spalte von 111 Pixeln fuer 252 Pixel Aufschrift -
                141 Pixel Ueberstand, bei JEDER Fensterbreite. Ein Raster ist
                richtig, wo Kanten UNTEREINANDER stehen sollen (die Knopfleiste
                in einer Tabellenzelle); eine Werkzeugleiste ist eine ZEILE, die
                umbricht. Dritter Fall derselben Verwechslung nach der
                Filterleiste. Nachgemessen im Browser, nicht gerechnet. */}
            <div className="reihe" style={{ marginBottom: 10 }}>
              <button className="knopf" onClick={laden} disabled={laeuft || lauf?.laeuft}>
                Neu einlesen
              </button>
              <button className="knopf haupt" onClick={alleUebertragen}
                      disabled={laeuft || lauf?.laeuft || !zuUebertragen}>
                {lauf?.laeuft
                  ? `Läuft: ${lauf.ordner_fertig} von ${lauf.ordner_gesamt}`
                  : anlegen.length
                    ? `${zuUebertragen} Ordner übertragen (${anlegen.length} Aufträge anlegen)`
                    : `${bereit.length} zugeordnete Ordner übertragen`}
              </button>
              {lauf?.laeuft && !lauf.halt && (
                <button className="knopf" onClick={anhalten}>
                  Nach diesem Ordner anhalten
                </button>
              )}
            </div>

            {/* WO STEHT ER GERADE?
             *
             * ORIGINALTEXT MARVIN (06.09.2026)
             *   „er zeigt mir nicht den aktellen stand an wo er dran ist. ich
             *    kann es auch nicht anhalten."
             *
             * DER STAND KOMMT VOM SERVER UND NICHT AUS DIESEM FENSTER. Wer
             * die Seite verlässt und wiederkommt, sieht denselben Stand; wer
             * auf einem zweiten Rechner nachsieht, auch. Der Balken ist eine
             * Fläche in Prozent und keine Bibliothek. */}
            {lauf && (
              <div className={'hinweis' + (lauf.abgerissen ? ' warnung' : '')}
                   style={{ marginBottom: 10 }}>
                <b>
                  {lauf.laeuft ? (lauf.halt ? 'Hält an' : 'Der Lauf arbeitet')
                    : lauf.abgerissen ? 'Der Lauf ist abgerissen'
                    : lauf.angehalten_am ? 'Angehalten' : 'Der letzte Lauf'}
                </b>
                <div className="klein">{lauf.klartext}</div>
                {lauf.ordner_gesamt > 0 && (
                  <div className="balken dick" style={{ marginTop: 8 }}>
                    <div style={{
                      width: Math.min(100, Math.round(
                        lauf.ordner_fertig / lauf.ordner_gesamt * 100)) + '%',
                      background: lauf.abgerissen ? 'var(--ueberfaellig)'
                        : lauf.laeuft ? 'var(--laufend)' : 'var(--erledigt)',
                    }} />
                  </div>
                )}
                {lauf.halt && lauf.laeuft && (
                  <p className="klein leise" style={{ margin: '6px 0 0' }}>
                    Er hält an, sobald der Ordner fertig ist, an dem er gerade
                    sitzt. Mitten im Ordner abzubrechen hieße, ihn halb geleert
                    stehenzulassen - und das fällt niemandem auf.
                  </p>
                )}
                {lauf.abgerissen && (
                  <p className="klein" style={{ margin: '6px 0 0' }}>
                    Seit {zeitpunkt(lauf.zuletzt_am)} kam nichts mehr: der
                    Server ist neu gestartet, während der Lauf lief. Was
                    übertragen ist, ist übertragen - der Rest liegt noch da.
                    Ein neuer Lauf macht dort weiter.
                  </p>
                )}
                {!!lauf.fehler && (
                  <p className="klein warnung" style={{ margin: '6px 0 0' }}>
                    {lauf.fehler}
                  </p>
                )}
                {/* NUR DIE ORDNER MIT BEFUND. Vierhundert grüne Zeilen sind
                    kein Bericht, sondern eine Wand; was geklemmt hat, ist die
                    Auskunft. */}
                {!!lauf.befund?.length && (
                  <details style={{ marginTop: 8 }}>
                    <summary>{lauf.befund.length} Ordner mit Befund</summary>
                    <div className="rollt">
                      <table className="tabelle liste">
                        <thead><tr><th>Ordner</th><th>Was</th></tr></thead>
                        <tbody>
                          {lauf.befund.map((b: any, i: number) => (
                            <tr key={i}>
                              <td>{b.ordner}</td>
                              <td className="klein">
                                {b.angelegt && <div>{b.angelegt}</div>}
                                {b.fehler ? <span className="warnung">{b.fehler}</span>
                                  : `${b.gelesen} gelesen, ${b.abgelegt} abgelegt`
                                    + (b.schon_da ? `, ${b.schon_da} schon da` : '')}
                                {!!b.offen?.length && (
                                  <button className="knopf klein" style={{ marginLeft: 8 }} disabled={laeuft || lauf?.laeuft}
                                          onClick={() => restWeg(b.ordner, b.offen)}>Übrige verwerfen</button>
                                )}
                                {!!b.offen?.length && (
                                  <ul className="klein leise">
                                    {b.offen.slice(0, 10).map((o: any, k: number) => (
                                      <li key={k}>{o.pfad}: {o.grund}</li>
                                    ))}
                                  </ul>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                )}
              </div>
            )}

            {/* WAS DER LAUF ANLEGT. Vorher sichtbar und nicht erst im Befund:
                wer hundert Aufträge anlegen lässt, will vorher sehen, welche. */}
            {!!anlegen.length && (
              <details open style={{ marginBottom: 10 }}>
                <summary><b>Auftrag wird angelegt ({anlegen.length})</b></summary>
                <div className="rollt">
                  <table className="tabelle liste">
                    <thead><tr><th>Ordner</th><th className="zahl">Dateien</th>
                      <th>Was passiert</th><th></th></tr></thead>
                    <tbody>
                      {anlegen.map((o) => (
                        <tr key={o.name}>
                          <td>{o.name}</td>
                          <td className="zahl">{o.dateien}</td>
                          <td className="leise">{o.anlage.text}</td>
                          <td className="knopfleiste">
                            <button className="knopf" disabled={laeuft || lauf?.laeuft}
                                    onClick={() => einer(o.name)}>
                              Nur diesen
                            </button>
                            <button className="knopf" disabled={laeuft || lauf?.laeuft}
                                    onClick={() => setWaehlen(o.name)}>
                              Anderer Auftrag
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            {/* WAS OHNE AUFTRAG DASTEHT - und der Weg, es zu beheben.
                Marvins Punkt 5: „bei offenen aufträgen bringst du mir eine
                liste und ich helfe dir beim finden des richtigen ordners im
                transferverzeichnis durch eine art «ordner auswählen»." */}
            {!!offen.length && (
              <>
                <p className="klein"><b>Ohne Auftrag ({offen.length})</b> - hier
                  lässt sich der Auftrag nicht sicher aus dem Namen anlegen.
                  Diese Ordner werden erst übertragen, wenn jemand sagt, wohin
                  sie gehören.</p>
                <div className="rollt">
                  <table className="tabelle liste">
                    <thead><tr><th>Ordner</th><th className="zahl">Dateien</th>
                      <th>Warum nicht</th><th></th></tr></thead>
                    <tbody>
                      {offen.map((o) => (
                        <tr key={o.name}>
                          <td>{o.name}</td>
                          <td className="zahl">{o.dateien}</td>
                          <td className="leise">{o.zuordnung.grund}</td>
                          <td className="knopfleiste">
                            <button className="knopf" disabled={laeuft || lauf?.laeuft}
                                    onClick={() => setWaehlen(o.name)}>
                              Auftrag auswählen
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {!!bereit.length && (
              <details style={{ marginTop: 10 }}>
                <summary>{bereit.length} Ordner mit Auftrag</summary>
                <div className="leib rollt">
                  <table className="tabelle liste">
                    <thead><tr><th>Ordner</th><th className="zahl">Dateien</th>
                      <th>Zuordnung</th><th></th></tr></thead>
                    <tbody>
                      {bereit.map((o) => (
                        <tr key={o.name}>
                          <td>{o.name}</td>
                          <td className="zahl">{o.dateien}</td>
                          <td className="leise">{o.zuordnung.wie}</td>
                          {/* WAEHREND DER MASSENLAUF ARBEITET, FASST HIER
                              NIEMAND EINEN ORDNER AN. Zwei Wege auf dieselbe
                              Datei raeumen sich gegenseitig die Quelle weg. */}
                          <td className="knopfleiste">
                            <button className="knopf" disabled={laeuft || lauf?.laeuft}
                                    onClick={() => einer(o.name)}>
                              Nur diesen
                            </button>
                            {o.vonHand && (
                              <button className="knopf" disabled={laeuft || lauf?.laeuft}
                                      onClick={async () => {
                                        await api.transferZuordnen(o.name, null);
                                        await laden();
                                      }}>
                                Zuordnung lösen
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
          </>
        )}

        {/* WAS DER LAUF GETAN HAT. Er steht hier und nicht in einer Meldung,
            die nach fünf Sekunden weggeht: bei vierhundert Ordnern will man
            hinterher wissen, welche hakten. */}
        {!!ergebnisse.length && (
          <div className="rollt" style={{ marginTop: 12 }}>
            <table className="tabelle liste">
              <thead><tr><th>Ordner</th><th>Ergebnis</th></tr></thead>
              <tbody>
                {ergebnisse.map((e, i) => (
                  <tr key={i}>
                    <td>{e.ordner}</td>
                    <td className={e.fehler ? 'warnung' : ''}>
                      {e.klartext}
                      {!!e.offen?.length && (
                        <button className="knopf klein" style={{ marginLeft: 8 }} disabled={laeuft}
                                onClick={() => restWeg(e.ordner, e.offen)}>Übrige verwerfen und Ordner leeren</button>
                      )}
                      {!!e.offen?.length && (
                        <ul className="klein leise">
                          {e.offen.slice(0, 10).map((o: any, k: number) => (
                            <li key={k}>{o.pfad}: {o.grund}</li>
                          ))}
                        </ul>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {waehlen && (
        <OrdnerZuordnen
          ordner={waehlen}
          zu={() => setWaehlen(null)}
          fertig={async () => { setWaehlen(null); await laden(); }}
        />
      )}
    </div>
  );
}


/**
 * EINEN ORDNER VON HAND EINEM AUFTRAG GEBEN.
 *
 * ORIGINALTEXT MARVIN (05.09.2026)
 *   „bei offenen aufträgen bringst du mir eine liste und ich helfe dir beim
 *    finden des richtigen ordners im transferverzeichnis durch eine art
 *    «ordner auswählen»."
 *
 * **DIE LISTE ZEIGT AUFTRÄGE OHNE EINE EINZIGE DATEI**, und das ist ein
 * ZUSTAND und keine Aussage über einen Lauf: „stand im letzten Report" wäre
 * am nächsten Tag falsch. Je Zeile steht der Ordnername, den die alte Ablage
 * dafür trägt - genau der, den er in seinem Verzeichnis sieht.
 *
 * Der Suchtext geht über Anschrift, Ort und beide Kennungen. Er filtert im
 * Browser: die Liste ist ein paar hundert Zeilen lang, und eine Anfrage je
 * Tastendruck wäre die Sorte Last, wegen der es seit dem 02.09.2026 eine
 * Schlange vor der Datenbank gibt.
 */
function OrdnerZuordnen({ ordner, zu, fertig }: any) {
  const [zeilen, setZeilen] = useState<any[]>([]);
  const [suche, setSuche] = useState('');
  const [laeuft, setLaeuft] = useState(true);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);

  useEffect(() => {
    (async () => {
      try { setZeilen((await api.transferOffen()).zeilen); }
      catch (f) { setFehler(ausFehler(f)); }
      finally { setLaeuft(false); }
    })();
  }, []);

  const s = suche.trim().toLowerCase();
  const gezeigt = (s
    ? zeilen.filter((z) => `${z.anschrift} ${z.ort} ${z.kls_id} ${z.sm_nummer}`
        .toLowerCase().includes(s))
    : zeilen).slice(0, 200);

  async function nimm(id: string) {
    setLaeuft(true); setFehler(null);
    try { await api.transferZuordnen(ordner, id); await fertig(); }
    catch (f) { setFehler(ausFehler(f)); setLaeuft(false); }
  }

  return (
    <Dialog titel="Auftrag auswählen" untertitel={ordner} zu={zu} breite={860}>
      <Fehlerkasten lage={fehler} />
      <p className="klein leise" style={{ marginTop: 0 }}>
        Aufträge, an denen noch keine einzige Datei hängt. Der Ordnername
        daneben ist der, den die alte Ablage dafür trägt.
      </p>
      <label className="feld">
        <span className="titel">Suchen</span>
        <input value={suche} onChange={(e) => setSuche(e.target.value)}
               autoComplete="off"
               placeholder="Straße, Ort, KLS-ID oder SM-Nummer" />
      </label>
      {laeuft && <p className="klein leise">Einen Moment.</p>}
      {!laeuft && !gezeigt.length && (
        <p className="klein leise">
          {zeilen.length
            ? 'Kein Auftrag passt zu dieser Suche.'
            : 'An jedem Auftrag hängt schon mindestens eine Datei.'}
        </p>
      )}
      <div className="rollt">
        <table className="tabelle liste">
          <tbody>
            {gezeigt.map((z) => (
              <tr key={z.id}>
                <td>
                  <b>{z.anschrift}</b><br />
                  <span className="klein leise">{z.ort}, {z.ordner}</span>
                </td>
                <td className="knopfleiste">
                  <button className="knopf haupt" disabled={laeuft}
                          onClick={() => nimm(z.id)}>
                    Diesen nehmen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {gezeigt.length >= 200 && (
        <p className="klein leise">
          Es werden 200 Zeilen gezeigt. Grenz die Suche ein.
        </p>
      )}
    </Dialog>
  );
}

/**
 * ============================================================================
 * PAPIER UND DATEI ZUSAMMENBRINGEN - der Blick auf den Bestand.
 * ============================================================================
 * ORIGINALTEXT MARVIN (07.09.2026)
 *   „was ist jetzt zu der frage heute mit den fehlenden dokumenten und
 *    verlinkungsfehlern bei bildern und pdf. da habe ich keine antwort
 *    erhalten. das problem ist nicht gelöst!"
 *
 * DIE ZUORDNUNG LÄUFT AN DREI STELLEN VON SELBST: beim Hochladen sucht die
 * neue Datei ihr Papier, beim Erfassen sucht das neue Papier seine Datei, und
 * die Ordner-Übernahme ruft sie am Ende jedes Auftragsordners. **Von der
 * Übernahme aus der alten Tabelle wurde sie nicht gerufen** - und genau die
 * legt die Behördenpapiere aus den Haken an. Das ist nachgetragen; diese
 * Karte ist der Blick auf das, was schon dasteht.
 *
 * SIE STEHT HIER UND NICHT IM SYSTEMZUSTAND, weil hier die Arbeit passiert,
 * aus der die Lücke entsteht: eine Tabelle einlesen, einen Ordnerbaum
 * abwerfen. Wer die eine Sache tut, sieht die andere daneben.
 *
 * UND SIE IST EIN KNOPF UND KEIN TAKT (Marvin, 01.09.2026: „die ganzen
 * trigger und automatiken fallen hier bei dem livesystem doch alle komplett
 * weg"). Gedrückt wird er, wenn sich etwas geändert hat.
 */
/**
 * DIE BAUAKTEN, DIE NIE ZU ENDE GELESEN WURDEN.
 *
 * ORIGINALTEXT MARVIN (08.09.2026)
 *   „weiter wurde festgestellt, dass die informationen unter technik nicht aus
 *    der bauakte ausgelesen werden, das musst du beheben und einmal für alle
 *    laufen lassen"
 *
 * DER FEHLER WAR EINE ZEILE UND SEIN SCHADEN DER GANZE BLOCK. Das Auslesen
 * schrieb seinen Eintrag in die Auftragsinfo mit der Quelle „automatik", und
 * `ereignis_quelle` kennt diesen Wert nicht - die INSERT stand IN DERSELBEN
 * Transaktion wie das Füllen der Felder, also fiel alles zurück. **Und nur auf
 * dem automatischen Weg:** von Hand zugeordnet gilt „person", und dann
 * funktioniert es. Ein Fehler, der nur den unbeaufsichtigten Weg trifft, fällt
 * bei jedem Ausprobieren durch.
 *
 * DIESE KARTE IST DER BLICK AUF DEN BESTAND. Eine Behebung gilt sonst nur für
 * das, was ab morgen hereinkommt - dieselbe Regel wie beim „zweiten Blick" im
 * Posteingang.
 */
export function BauaktenLesen() {
  const [zahl, setZahl] = useState<any>(null);
  const [probe, setProbe] = useState<any>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);

  async function zaehlen() {
    try { setZahl(await api.bauaktenUngelesen()); } catch { /* kein Recht */ }
  }
  useEffect(() => { zaehlen(); }, []);

  async function lauf(anwenden: boolean) {
    setLaeuft(true); setFehler(null);
    try {
      const e = await api.bauaktenLesen(anwenden);
      setProbe(e);
      if (anwenden) await zaehlen();
    } catch (f) { setFehler(ausFehler(f)); }
    finally { setLaeuft(false); }
  }

  /* Steht nichts offen, steht die Karte gar nicht erst da. */
  if (!zahl || !zahl.ungelesen) return null;

  return (
    <div className="karte" style={{ marginTop: 14 }}>
      <h3>Bauakten auslesen</h3>
      <div className="leib">
        <p className="leise klein" style={{ marginTop: 0 }}>
          {/* DIE BEZUGSGRÖSSE STEHT DANEBEN. „14 ungelesen" allein liest sich
              wie ein Datenverlust; „von 27, die daliegen" sagt, dass der Weg
              im Grundsatz arbeitet. */}
          <b>{zahl.ungelesen} Bauakten</b> von {zahl.mit_akte}, die im
          Auftragsordner liegen, sind nie zu Ende gelesen worden. Der Knopf
          holt NVt, NVt-Standort, Rohrverband, Röhrchen, Röhrchenbreite und
          ONKz aus dem Deckblatt in den Block „Technik".
        </p>
        <p className="klein leise">
          Kundendaten werden dabei NICHT überschrieben: der erfasste Wert
          stammt meist vom Kunden selbst, das Deckblatt ist Monate alt. Eine
          Abweichung wird gemeldet und steht im Befund.
          {zahl.stumm > 0 && (
            <> Dazu {zahl.stumm} Akten, aus denen sich nichts lesen liess
              (reiner Scan oder fremdes Format) - die stehen auf der
              Abtippliste und werden hier nicht noch einmal geöffnet.</>
          )}
        </p>

        <Fehlerkasten lage={fehler} />

        <div className="reihe" style={{ marginTop: 10 }}>
          <button className="knopf" disabled={laeuft}
                  onClick={() => lauf(false)}>
            Nachsehen
          </button>
          {probe && !probe.anwenden && probe.gelesen > 0 && (
            <button className="knopf haupt" disabled={laeuft}
                    onClick={() => lauf(true)}>
              {probe.gelesen} auslesen
            </button>
          )}
        </div>

        {laeuft && <p className="klein leise">Wird gelesen …</p>}

        {probe && !laeuft && (
          <div className="hinweis" style={{ marginTop: 10 }}>
            <b>{probe.klartext}</b>
            {probe.offen.length > 0 && (
              <ul className="klein" style={{ margin: '6px 0 0 18px' }}>
                {probe.offen.map((o: any) => (
                  <li key={o.grund}>{o.anzahl} × {o.grund}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
